import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type Plan = {
  id: string;
  name: string;
  description: string;
  monthly_price: number;
  annual_price: number;
  message_limit: number;
  features: string[];
  is_active?: boolean;
};

const defaultPlans: Plan[] = [
  {
    id: 'starter',
    name: 'Starter',
    description: 'Perfect for small teams getting started',
    monthly_price: 29.97,
    annual_price: 287.71,
    message_limit: 300,
    features: ['AI-powered responses', 'Basic analytics', 'Email support', '2 phone numbers', 'Unused Text Credits roll over for 1 full year!'],
    is_active: true
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'Ideal for growing businesses',
    monthly_price: 49.97,
    annual_price: 479.71,
    message_limit: 600,
    features: ['Everything in Starter', 'Priority AI routing', 'Advanced analytics', 'Multi-agent support', 'Priority support', 'Up to 4 phone numbers', 'Unused Text Credits roll over for 1 full year!'],
    is_active: true
  },
  {
    id: 'business',
    name: 'Business',
    description: 'For high-volume operations',
    monthly_price: 79.97,
    annual_price: 767.71,
    message_limit: 1000,
    features: ['Everything in Professional', 'Custom AI training', 'API access', 'Dedicated support', 'Up to 7 phone numbers', 'Unused Text Credits roll over for 1 full year!'],
    is_active: true
  }
];

interface PricingPlansProps {
  onPlanSelect?: (planId: string, billingCycle: 'monthly' | 'annual') => void;
}

export const PricingPlans = ({ onPlanSelect }: PricingPlansProps) => {
  const queryClient = useQueryClient();

  const { data: plans, isLoading } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('monthly_price');
      
      if (error) throw error;
      return data;
    }
  });

  const { data: currentSubscription } = useQuery({
    queryKey: ['current-subscription'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*, subscription_plans(*)')
        .eq('organization_id', user.id)
        .eq('status', 'active')
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    }
  });

  const subscribeMutation = useMutation({
    mutationFn: async ({ planId, billingCycle }: { planId: string; billingCycle: 'monthly' | 'annual' }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const plan = plans?.find(p => p.id === planId);
      if (!plan) throw new Error('Plan not found');

      const periodEnd = new Date();
      if (billingCycle === 'monthly') {
        periodEnd.setMonth(periodEnd.getMonth() + 1);
      } else {
        periodEnd.setFullYear(periodEnd.getFullYear() + 1);
      }

      const { error } = await supabase
        .from('subscriptions')
        .insert({
          organization_id: user.id,
          plan_id: planId,
          billing_cycle: billingCycle,
          status: 'active',
          current_period_start: new Date().toISOString(),
          current_period_end: periodEnd.toISOString()
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['current-subscription'] });
      toast.success('Subscription activated successfully!');
    },
    onError: (error) => {
      toast.error(`Failed to subscribe: ${error.message}`);
    }
  });

  if (isLoading) {
    return <div className="text-center py-8">Loading plans...</div>;
  }

  return (
    <div className="w-full max-w-7xl mx-auto p-6">
      <div className="text-center mb-12">
        <h2 id="pricing-heading" className="text-4xl font-bold mb-4">Choose Your Plan</h2>
        <p className="text-muted-foreground text-lg">
          Select the perfect plan for your business needs
        </p>
        <div className="mt-4">
          <Badge variant="outline" className="text-sm px-4 py-2">
            Save 20% with annual billing
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
        {(plans && plans.length > 0 ? plans : defaultPlans).map((plan) => {
          const rawFeatures = Array.isArray(plan.features) ? plan.features : [];
          const features = (() => {
            if (plan.name === 'Starter') {
              return rawFeatures.map((f: string) => f === '1 phone number' ? '2 phone numbers' : f);
            }
            if (plan.name === 'Professional') {
              return rawFeatures.map((f: string) => f === 'Up to 3 phone numbers' ? 'Up to 4 phone numbers' : f);
            }
            if (plan.name === 'Business') {
              return rawFeatures.filter((f: string) => f !== 'Custom integrations');
            }
            return rawFeatures;
          })();
          const isCurrentPlan = currentSubscription?.plan_id === plan.id;
          const isProfessional = plan.name === 'Professional';
          const monthlyPrice = Number(plan.monthly_price);
          const annualPrice = Number(plan.annual_price);
          const annualMonthlyPrice = annualPrice / 12;

          return (
            <Card 
              key={plan.id} 
              className={`relative w-full ${
                isCurrentPlan 
                  ? "border-primary shadow-lg" 
                  : isProfessional 
                  ? "border-primary shadow-xl" 
                  : ""
              }`}
            >
              {isProfessional && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <Badge className="bg-blue-700 text-white hover:bg-blue-800 px-4 py-1">
                    Most Popular
                  </Badge>
                </div>
              )}
              <CardHeader>
                <div className="flex justify-between items-start mb-2">
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  {isCurrentPlan && <Badge>Current Plan</Badge>}
                </div>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <div>
                  <div className="text-4xl font-bold">
                    ${monthlyPrice}
                    <span className="text-lg font-normal text-muted-foreground">/mo</span>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    or ${annualMonthlyPrice.toFixed(2)}/mo billed annually
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="font-semibold">{plan.message_limit.toLocaleString()} messages/month</div>
                  <div className="text-sm text-muted-foreground">
                    Additional messages at $0.09 each
                  </div>
                </div>

                <div className="space-y-3">
                  {features.map((feature: string, index: number) => (
                    <div key={index} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>

              <CardFooter className="flex flex-col gap-2">
                <Button
                  className="w-full"
                  variant={isCurrentPlan ? "outline" : "default"}
                  disabled={isCurrentPlan || subscribeMutation.isPending}
                  onClick={() => {
                    if (onPlanSelect) {
                      onPlanSelect(plan.id, 'monthly');
                    } else {
                      subscribeMutation.mutate({ planId: plan.id, billingCycle: 'monthly' });
                    }
                  }}
                >
                  {isCurrentPlan ? 'Current Plan' : 'Start Monthly'}
                </Button>
                <Button
                  className="w-full"
                  variant="outline"
                  disabled={isCurrentPlan || subscribeMutation.isPending}
                  onClick={() => {
                    if (onPlanSelect) {
                      onPlanSelect(plan.id, 'annual');
                    } else {
                      subscribeMutation.mutate({ planId: plan.id, billingCycle: 'annual' });
                    }
                  }}
                >
                  Start Annual (Save 20%)
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
